import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getAffiliateEarnings, calculateAffiliatePayments } from '../influencer/services/affiliateService';

/**
 * Custom hook for managing earnings data
 * Provides real-time earnings data with caching and error handling
 */
export const useEarnings = () => {
  const user = useSelector((state) => state.user.data);
  const [earnings, setEarnings] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState({
    totalDue: 0,
    amountPaid: 0,
    amountUnpaid: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [calculationData, setCalculationData] = useState(null);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  /**
   * Get user ID from multiple sources
   */
  const getUserId = useCallback(() => {
    let userId = localStorage.getItem('userId');

    // If not in localStorage, try to get from Redux state
    if (!userId && user?._id) {
      userId = user._id;
      localStorage.setItem('userId', userId);
    }

    // If still no userId, try to get from userData in localStorage
    if (!userId) {
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const parsedUserData = JSON.parse(userData);
          userId = parsedUserData._id;
          if (userId) {
            localStorage.setItem('userId', userId);
          }
        } catch (parseError) {
          console.error('Error parsing userData from localStorage:', parseError);
        }
      }
    }

    return userId;
  }, [user]);

  /**
   * Check if we should refetch data based on cache
   */
  const shouldRefetch = useCallback(() => {
    if (!lastFetchTime) return true;
    const now = Date.now();
    return (now - lastFetchTime) > CACHE_DURATION;
  }, [lastFetchTime]);

  /**
   * Calculate totals from earnings data
   */
  const calculateTotals = useCallback((earningsData) => {
    return earningsData
      .filter(earning => earning.brandName && earning.campaignName)
      .reduce((acc, curr) => ({
        totalDue: acc.totalDue + (curr.totalDue || 0),
        amountPaid: acc.amountPaid + (curr.amountPaid || 0),
        amountUnpaid: acc.amountUnpaid + (curr.amountUnpaid || 0)
      }), { totalDue: 0, amountPaid: 0, amountUnpaid: 0 });
  }, []);

  /**
   * Fetch earnings data
   */
  const fetchEarnings = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && !shouldRefetch()) {
      return; // Use cached data
    }

    try {
      setLoading(true);
      setError(null);

      const userId = getUserId();
      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }

      // Validate userId format
      if (!/^[0-9a-fA-F]{24}$/.test(userId)) {
        throw new Error('Invalid user ID format. Please log in again.');
      }

      // console.log('Fetching earnings for user:', userId);
      const response = await getAffiliateEarnings(userId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch earnings');
      }

      setEarnings(response.data || []);
      setLastFetchTime(Date.now());

      // Calculate totals
      const totals = calculateTotals(response.data || []);
      setTotalEarnings(totals);

      // Show warning if there are invalid records
      const invalidRecords = (response.data || []).filter(earning => !earning.brandName || !earning.campaignName);
      if (invalidRecords.length > 0) {
        console.warn(`Found ${invalidRecords.length} earnings records with missing campaign or brand data`);
      }

    } catch (error) {
      console.error('Error fetching earnings:', error);
      setError(error.message || 'Failed to fetch earnings');
      
      // Set default values on error
      setEarnings([]);
      setTotalEarnings({ totalDue: 0, amountPaid: 0, amountUnpaid: 0 });
    } finally {
      setLoading(false);
    }
  }, [getUserId, shouldRefetch, calculateTotals]);

  /**
   * Calculate payments (triggers recalculation)
   */
  const calculatePayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = getUserId();
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await calculateAffiliatePayments(userId);

      if (response.success) {
        setCalculationData(response.data);

        // Update earnings with calculated data
        if (response.data.earnings && response.data.earnings.length > 0) {
          setEarnings(response.data.earnings);
          const totals = calculateTotals(response.data.earnings);
          setTotalEarnings(totals);
        } else {
          // If no earnings data, reset to empty
          setEarnings([]);
          setTotalEarnings({ totalDue: 0, amountPaid: 0, amountUnpaid: 0 });
        }

        setLastFetchTime(Date.now());
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to calculate payments');
      }
    } catch (error) {
      console.error('Error calculating payments:', error);
      setError(error.message || 'Failed to calculate payments');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [getUserId, calculateTotals]);

  /**
   * Refresh earnings data
   */
  const refreshEarnings = useCallback(() => {
    return fetchEarnings(true);
  }, [fetchEarnings]);

  // Auto-fetch earnings when user is available
  useEffect(() => {
    if (user?._id || getUserId()) {
      fetchEarnings();
    }
  }, [user, fetchEarnings, getUserId]);

  return {
    // Data
    earnings,
    totalEarnings,
    calculationData,
    
    // State
    loading,
    error,
    lastFetchTime,
    
    // Actions
    fetchEarnings,
    calculatePayments,
    refreshEarnings,
    
    // Computed values
    hasEarnings: earnings.length > 0,
    hasValidEarnings: earnings.filter(earning => earning.brandName && earning.campaignName).length > 0,
    isDataStale: shouldRefetch()
  };
};

export default useEarnings;
